import React, { useState, useEffect } from 'react';
import { 
  X, 
  Copy, 
  Check, 
  Server, 
  Laptop, 
  ShieldAlert, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  Globe, 
  Cpu, 
  Terminal,
  FileCode,
  Shield,
  Activity,
  Info
} from 'lucide-react';
import { ToolCall } from '../types';
import { formatTimeAgo } from '../services/timeAgo';

interface DetailDrawerProps {
  call: ToolCall | null;
  onClose: () => void;
}

function formatBytes(bytes?: number): string {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return 'N/A';
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 * 1000) return `${(bytes / 1000).toFixed(1)} KB`;
  return `${(bytes / (1000 * 1000)).toFixed(2)} MB`;
}

function highlightJsonHtml(json: any): string {
  let str: string;
  try {
    str = typeof json === 'string' ? JSON.stringify(JSON.parse(json), null, 2) : JSON.stringify(json, null, 2);
  } catch {
    str = typeof json === 'object' && json !== null ? JSON.stringify(json, null, 2) : String(json ?? '');
  }

  // Escape HTML characters
  const escaped = str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Match JSON tokens
  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    (match) => {
      let cls = 'text-amber-300'; // number default
      if (/^"/.test(match)) {
        if (/:$/.test(match.trim())) {
          cls = 'text-cyan-300 font-medium'; // key
        } else {
          cls = 'text-emerald-300'; // string value
        }
      } else if (/true|false/.test(match)) {
        cls = 'text-purple-400 font-semibold'; // boolean
      } else if (/null/.test(match)) {
        cls = 'text-slate-500 italic'; // null
      }
      return `<span class="${cls}">${match}</span>`;
    }
  );
}

export const DetailDrawer: React.FC<DetailDrawerProps> = ({ call, onClose }) => {
  const [copiedPayload, setCopiedPayload] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedTraceId, setCopiedTraceId] = useState(false);

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!call) return null;

  const handleCopy = (text: string, type: 'payload' | 'response' | 'id' | 'traceId') => {
    navigator.clipboard.writeText(text);
    if (type === 'payload') {
      setCopiedPayload(true);
      setTimeout(() => setCopiedPayload(false), 2000);
    } else if (type === 'response') {
      setCopiedResponse(true);
      setTimeout(() => setCopiedResponse(false), 2000);
    } else if (type === 'traceId') {
      setCopiedTraceId(true);
      setTimeout(() => setCopiedTraceId(false), 2000);
    } else {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const isMac = call.targetNode === 'Mac';
  const is200 = call.status === 200;
  const is403 = call.status === 403;
  const is409 = call.status === 409;

  // Telemetry metadata extraction
  const httpMethod = (call.httpMethod || (call.action ? call.action.split(' ')[0] : 'POST')).toUpperCase();
  const effectiveRequestId = call.requestId || (call.id && !call.id.startsWith('call_9r_') ? call.id : undefined);
  const formattedSize = formatBytes(call.responseBytes);
  const hasParameters = call.parameters && Object.keys(call.parameters).length > 0;
  const hasQueryParams = call.queryParams && Object.keys(call.queryParams).length > 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm transition-opacity duration-300">
      
      {/* Click outside to close */}
      <div className="absolute inset-0 cursor-pointer" onClick={onClose} />

      {/* Drawer content */}
      <aside 
        role="dialog"
        aria-label="Tool Call Details"
        className="relative w-full max-w-2xl h-full bg-[#0d131f] border-l border-slate-800 shadow-2xl flex flex-col z-10 overflow-hidden animate-in slide-in-from-right duration-200"
      >
        
        {/* Drawer Header */}
        <div className="p-5 border-b border-slate-800/80 bg-slate-900/80 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Status Code Badge */}
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-mono font-bold ${
                is200 ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' :
                is403 ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40 glow-rose' :
                'bg-amber-500/20 text-amber-300 border border-amber-500/40'
              }`}>
                {is200 && <CheckCircle2 className="w-3.5 h-3.5" />}
                {is403 && <ShieldAlert className="w-3.5 h-3.5" />}
                {is409 && <AlertTriangle className="w-3.5 h-3.5" />}
                {call.status} {call.statusText || (is200 ? 'OK' : is403 ? 'Forbidden' : 'Conflict')}
              </span>

              {/* HTTP Method Badge */}
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-bold ${
                httpMethod === 'GET' 
                  ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40'
                  : httpMethod === 'POST'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                  : httpMethod === 'DELETE'
                  ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                  : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
              }`}>
                {httpMethod}
              </span>

              {/* Target Node Badge */}
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-mono font-medium ${
                isMac 
                  ? 'bg-purple-950/70 text-purple-300 border border-purple-800/70'
                  : 'bg-cyan-950/70 text-cyan-300 border border-cyan-800/70'
              }`}>
                {isMac ? <Laptop className="w-3.5 h-3.5" /> : <Server className="w-3.5 h-3.5" />}
                <span>{isMac ? 'Mac (Apple Silicon)' : 'VPS (Linux)'}</span>
              </span>

              {/* Duration Badge */}
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                {call.durationMs}ms
              </span>

              {/* Response Bytes Badge (if present) */}
              {call.responseBytes !== undefined && (
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-cyan-300 border border-slate-700">
                  {formattedSize}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2 pt-1 font-mono text-xs text-slate-400">
              <span className="text-slate-300 font-semibold">{call.id}</span>
              <button 
                onClick={() => handleCopy(call.id, 'id')} 
                className="hover:text-cyan-400 transition-colors"
                title="Copy execution ID"
              >
                {copiedId ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
              <span className="text-slate-600">•</span>
              <div className="flex items-center gap-1.5 text-slate-300">
                <Clock className="w-3.5 h-3.5 text-cyan-400/80" />
                <span>{new Date(call.timestamp).toLocaleTimeString()}</span>
                <span className="px-1.5 py-0.5 rounded bg-cyan-950/70 text-cyan-300 border border-cyan-800/60 text-[10px] font-sans font-medium">
                  {formatTimeAgo(call.timestamp)}
                </span>
              </div>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close details"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-xs font-sans">
          
          {/* Security Guardrail Alert (if 403) */}
          {call.securityAudit && (
            <div className="rounded-lg bg-rose-950/30 border border-rose-800/70 p-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-rose-300 font-semibold">
                  <ShieldAlert className="w-4 h-4 text-rose-400" />
                  <span>Security Guardrail Interception</span>
                </div>
                <span className="px-2 py-0.5 rounded bg-rose-900/60 text-rose-200 border border-rose-700 font-mono text-[10px] font-bold">
                  {call.securityAudit.riskLevel} RISK
                </span>
              </div>
              <p className="text-rose-200/90 leading-relaxed">
                {call.securityAudit.violationReason}
              </p>
              <div className="pt-2 border-t border-rose-900/50 space-y-1 font-mono text-[11px]">
                <div className="text-slate-400">
                  <span className="text-rose-400">Policy: </span>
                  {call.securityAudit.policyId} ({call.securityAudit.ruleTriggered})
                </div>
                <div className="text-slate-400">
                  <span className="text-rose-400">Action: </span>
                  {call.securityAudit.mitigationAction}
                </div>
                {call.securityAudit.blockedPattern && (
                  <div className="text-slate-400">
                    <span className="text-rose-400">Pattern: </span>
                    <code className="bg-rose-950/80 px-1.5 py-0.5 rounded text-rose-300">
                      {call.securityAudit.blockedPattern}
                    </code>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conflict Alert (if 409) */}
          {is409 && call.errorMessage && (
            <div className="rounded-lg bg-amber-950/30 border border-amber-800/70 p-4 space-y-2">
              <div className="flex items-center gap-2 text-amber-300 font-semibold">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span>Resource Concurrency Conflict</span>
              </div>
              <p className="text-amber-200/90 leading-relaxed font-mono text-[11px]">
                {call.errorMessage}
              </p>
            </div>
          )}

          {/* Dedicated Network & Tracing Metrics Card */}
          <div className="rounded-lg bg-slate-900/80 border border-slate-800 p-4 space-y-3 font-mono text-xs">
            <div className="text-slate-400 font-sans font-semibold text-[11px] uppercase tracking-wider flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-3.5 h-3.5 text-cyan-400" />
                <span>Network & Tracing Metrics</span>
              </div>
              <span className={`px-2 py-0.5 rounded font-mono text-[11px] font-bold ${
                httpMethod === 'GET' 
                  ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40'
                  : httpMethod === 'POST'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                  : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
              }`}>
                {httpMethod}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
              <div>
                <span className="text-slate-400 text-[10px] block">HTTP Method</span>
                <span className="text-slate-200 font-bold">{httpMethod}</span>
              </div>
              <div>
                <span className="text-slate-400 text-[10px] block">Response Size</span>
                <span className="text-cyan-300 font-semibold">{formattedSize}</span>
              </div>
              <div>
                <span className="text-slate-400 text-[10px] block">Duration</span>
                <span className="text-slate-200">{call.durationMs}ms</span>
              </div>
              <div>
                <span className="text-slate-400 text-[10px] block">HTTP Status</span>
                <span className={is200 ? "text-emerald-400 font-semibold" : is403 ? "text-rose-400 font-semibold" : "text-amber-400 font-semibold"}>
                  {call.status} {call.statusText || 'OK'}
                </span>
              </div>
            </div>

            {/* Request / Trace ID with 1-click copy */}
            {effectiveRequestId && (
              <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between gap-2">
                <div className="truncate flex-1">
                  <span className="text-slate-400 text-[10px] block">Request / Trace ID (X-Request-ID)</span>
                  <span className="text-slate-300 font-mono text-[11px] select-all truncate block">
                    {effectiveRequestId}
                  </span>
                </div>
                <button
                  onClick={() => handleCopy(effectiveRequestId, 'traceId')}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-mono transition-colors shrink-0"
                  title="Copy Request ID"
                >
                  {copiedTraceId ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-300">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>Copy ID</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Action & Endpoint Meta Card */}
          <div className="rounded-lg bg-slate-900/80 border border-slate-800 p-4 space-y-3 font-mono">
            <div className="text-slate-400 font-sans font-semibold text-[11px] uppercase tracking-wider flex items-center gap-2">
              <Terminal className="w-3.5 h-3.5 text-cyan-400" />
              <span>Action & Routing Endpoint</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <span className="text-slate-400 text-[10px] block">Tool Name</span>
                <span className="text-slate-200 font-bold text-sm text-cyan-300">{call.toolName}</span>
              </div>
              <div>
                <span className="text-slate-400 text-[10px] block">Action Identifier</span>
                <span className="text-slate-300 font-semibold">{call.action}</span>
              </div>
            </div>

            <div>
              <span className="text-slate-400 text-[10px] block mb-1">Target Endpoint</span>
              <div className="px-2.5 py-1.5 rounded bg-slate-950 border border-slate-800 text-slate-300 font-mono text-xs select-all">
                {call.endpoint}
              </div>
            </div>
          </div>

          {/* Target Host & Client Machine Info */}
          <div className="rounded-lg bg-slate-900/80 border border-slate-800 p-4 space-y-3 font-mono text-xs">
            <div className="text-slate-400 font-sans font-semibold text-[11px] uppercase tracking-wider flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-purple-400" />
              <span>Target Node Topology & Client Origin</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]">
              <div>
                <span className="text-slate-400 text-[10px] block">Target Host</span>
                <span className="text-slate-200">{call.nodeDetails.host}</span>
              </div>
              <div>
                <span className="text-slate-400 text-[10px] block">Internal Mesh IP</span>
                <span className="text-slate-200">{call.nodeDetails.ip}</span>
              </div>
              <div>
                <span className="text-slate-400 text-[10px] block">Operating System</span>
                <span className="text-slate-300">{call.nodeDetails.os} ({call.nodeDetails.arch})</span>
              </div>
              <div>
                <span className="text-slate-400 text-[10px] block">Caller / Client IP</span>
                <span className="text-slate-300">{call.clientIp}</span>
              </div>
            </div>

            {call.userAgent && (
              <div className="pt-2 border-t border-slate-800/80">
                <span className="text-slate-400 text-[10px] block">Client User Agent</span>
                <span className="text-slate-400 text-[10px] truncate block">{call.userAgent}</span>
              </div>
            )}
          </div>

          {/* Parameters / Input Section */}
          <div className="rounded-lg bg-slate-900/80 border border-slate-800 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-950/80 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2 font-mono text-slate-300 text-xs">
                <FileCode className="w-3.5 h-3.5 text-cyan-400" />
                <span>Input Parameters (JSON)</span>
              </div>
              {hasParameters && (
                <button
                  onClick={() => handleCopy(JSON.stringify(call.parameters, null, 2), 'payload')}
                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-mono transition-colors"
                >
                  {copiedPayload ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-300">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>Copy JSON</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Content for Input Parameters */}
            {hasParameters && hasQueryParams ? (
              <div className="p-4 bg-[#070b13] space-y-4">
                {/* Query Parameters First */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400">
                    <Globe className="w-3.5 h-3.5 text-sky-400" />
                    <span className="uppercase tracking-wider font-semibold text-slate-400">Query Parameters</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(call.queryParams!).map(([k, v]) => (
                      <div key={k} className="inline-flex items-center rounded-md bg-slate-950 border border-slate-800 text-xs overflow-hidden shadow-sm">
                        <span className="px-2.5 py-1 bg-sky-950/70 text-sky-300 font-mono font-semibold border-r border-slate-800">
                          {k}
                        </span>
                        <span className="px-2.5 py-1 text-slate-200 font-mono select-all">
                          {String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Request Body (JSON) */}
                <div className="space-y-2 pt-3 border-t border-slate-800/80">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400">
                      <FileCode className="w-3.5 h-3.5 text-cyan-400" />
                      <span className="uppercase tracking-wider font-semibold text-slate-400">Request Body (JSON)</span>
                    </div>
                    <button
                      onClick={() => handleCopy(JSON.stringify(call.parameters, null, 2), 'payload')}
                      className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-mono transition-colors"
                      title="Copy formatted JSON parameters"
                    >
                      {copiedPayload ? (
                        <>
                          <Check className="w-3 h-3 text-emerald-400" />
                          <span className="text-emerald-300">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <pre
                    className="p-3 text-[11px] font-mono text-slate-300 overflow-x-auto bg-slate-950 rounded border border-slate-800/90 max-h-60 leading-relaxed select-text"
                    dangerouslySetInnerHTML={{ __html: highlightJsonHtml(call.parameters) }}
                  />
                </div>
              </div>
            ) : hasQueryParams && !hasParameters ? (
              <div className="p-4 bg-[#070b13] space-y-3">
                <div className="flex items-center gap-2 text-slate-400 text-xs font-mono">
                  <Info className="w-4 h-4 text-sky-400 shrink-0" />
                  <span>HTTP GET / URL Query Parameters (no request body payload)</span>
                </div>

                <div className="space-y-2 pt-2 border-t border-slate-800/60">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(call.queryParams!).map(([k, v]) => (
                      <div key={k} className="inline-flex items-center rounded-md bg-slate-950 border border-slate-800 text-xs overflow-hidden shadow-sm">
                        <span className="px-2.5 py-1 bg-sky-950/70 text-sky-300 font-mono font-semibold border-r border-slate-800">
                          {k}
                        </span>
                        <span className="px-2.5 py-1 text-slate-200 font-mono select-all">
                          {String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : hasParameters ? (
              <pre
                className="p-4 text-[11px] font-mono text-slate-300 overflow-x-auto bg-[#070b13] max-h-60 leading-relaxed select-text"
                dangerouslySetInnerHTML={{ __html: highlightJsonHtml(call.parameters) }}
              />
            ) : (
              <div className="p-4 bg-[#070b13] text-xs font-mono text-slate-400 flex items-center gap-2">
                <Info className="w-4 h-4 text-slate-400 shrink-0" />
                <span>
                  {httpMethod === 'GET'
                    ? 'HTTP GET request (no request body or query parameters)'
                    : 'No input parameters provided in request body'}
                </span>
              </div>
            )}
          </div>

          {/* Response Payload JSON Viewer */}
          <div className="rounded-lg bg-slate-900/80 border border-slate-800 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-950/80 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2 font-mono text-slate-300 text-xs">
                <Terminal className="w-3.5 h-3.5 text-purple-400" />
                <span>Response Payload (JSON)</span>
              </div>
              {call.responsePayload && (
                <button
                  onClick={() => handleCopy(
                    typeof call.responsePayload === 'string' 
                      ? call.responsePayload 
                      : JSON.stringify(call.responsePayload, null, 2), 
                    'response'
                  )}
                  className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-mono transition-colors"
                >
                  {copiedResponse ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-300">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>Copy Response</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {call.responsePayload ? (
              <pre 
                className="p-4 text-[11px] font-mono text-slate-300 overflow-x-auto bg-[#070b13] max-h-60 leading-relaxed select-text"
                dangerouslySetInnerHTML={{ __html: highlightJsonHtml(call.responsePayload) }}
              />
            ) : (
              <div className="p-4 bg-[#070b13] text-xs font-mono space-y-1.5">
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="font-semibold">
                    Status: {call.status} {call.statusText || 'OK'}
                    {call.responseBytes !== undefined && ` • ${formattedSize} transferred`}
                  </span>
                </div>
                <p className="text-slate-400 text-[11px] leading-relaxed">
                  Full response body not captured in audit log for efficiency.
                </p>
              </div>
            )}
          </div>

        </div>

        {/* Drawer Footer */}
        <div className="p-4 border-t border-slate-800/80 bg-slate-900/80 flex items-center justify-between text-xs font-mono text-slate-400">
          <span>Press <kbd className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">Esc</kbd> to close</span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors"
          >
            Dismiss
          </button>
        </div>

      </aside>
    </div>
  );
};
